import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Chip, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import { loadBase, loadPlayerStats, loadDefensiveStats } from '../../services/espnService';
import { useConfig } from '../../hooks/useConfig';

// ── Offensive computation (mirrors OffensiveTeamNR.js) ───────────────────────
const OFF_FORMULAS = {
  QB: ['=completionPct','passingYards','passingTouchdowns','=~interceptionPct','~sacks','rushingYards','rushingTouchdowns'],
  RB: ['rushingYards','=yardsPerRushAttempt','rushingTouchdowns','receivingYards','=yardsPerReception','receivingTouchdowns','~fumbles'],
  WR: ['receptions','receivingYards','receivingTouchdowns','receivingFirstDowns','rushingYards','rushingTouchdowns','~fumbles'],
  TE: ['receptions','receivingYards','receivingTouchdowns','receivingFirstDowns','rushingYards','rushingTouchdowns','~fumbles'],
};
const OFF_MIN_GAMES  = { QB: 8, RB: 6, WR: 6, TE: 6 };
const OFF_POS_SLOTS  = { QB: 1, RB: 2, WR: 3, TE: 2 };
const OFF_POSITIONS  = ['QB','RB','WR','TE'];

function parseKey(key) {
  let k = key;
  const isRaw     = k.startsWith('='); if (isRaw)     k = k.slice(1);
  const isPenalty = k.startsWith('~'); if (isPenalty) k = k.slice(1);
  return { rawKey: k, isRaw, isPenalty };
}

function computeOffPosNR(pos, players, stats, weights = {}) {
  const formula   = OFF_FORMULAS[pos];
  const minGames  = OFF_MIN_GAMES[pos] ?? 6;
  const eligible  = players.filter(p => stats[p.id]?.gamesPlayed >= minGames).map(p => {
    const s = stats[p.id]; const gp = s.gamesPlayed;
    const perGame = {};
    formula.forEach(key => { const { rawKey, isRaw } = parseKey(key); perGame[key] = isRaw ? s[rawKey] ?? 0 : gp > 0 ? (s[rawKey] ?? 0) / gp : 0; });
    return { ...p, perGame };
  });
  const maxRank = eligible.length + 1;
  if (!eligible.length) { const m = {}; players.forEach(p => { m[p.id] = maxRank; }); return { nrMap: m, maxRank }; }
  const statRanks = {}; eligible.forEach(p => { statRanks[p.id] = {}; });
  formula.forEach(key => {
    const { isPenalty } = parseKey(key);
    const sorted = [...eligible].sort((a, b) => isPenalty ? a.perGame[key] - b.perGame[key] : b.perGame[key] - a.perGame[key]);
    let rank = 1, i = 0;
    while (i < sorted.length) {
      const tieVal = sorted[i].perGame[key]; let j = i;
      while (j < sorted.length && sorted[j].perGame[key] === tieVal) { statRanks[sorted[j].id][key] = rank; j++; }
      rank += j - i; i = j;
    }
  });
  const nrMap = {};
  const withNR = eligible.map(p => ({ ...p, rankSum: formula.reduce((acc, k) => acc + (statRanks[p.id][k] ?? eligible.length) * (weights[k] ?? 1), 0) })).sort((a, b) => a.rankSum - b.rankSum);
  let nr = 1, i = 0;
  while (i < withNR.length) {
    const tieSum = withNR[i].rankSum; let j = i;
    while (j < withNR.length && withNR[j].rankSum === tieSum) { nrMap[withNR[j].id] = nr; j++; } nr += j - i; i = j;
  }
  players.forEach(p => { if (!(p.id in nrMap)) nrMap[p.id] = maxRank; });
  return { nrMap, maxRank };
}

function computeTeamOffScores(allPlayers, stats, config) {
  const posData = {};
  OFF_POSITIONS.forEach(pos => {
    const posPlayers = Object.values(allPlayers).filter(p => p.posKey === pos);
    posData[pos] = computeOffPosNR(pos, posPlayers, stats, config?.[`nr_weights_off_${pos.toLowerCase()}`] ?? {});
  });
  const teamMap = {};
  Object.values(allPlayers).forEach(p => {
    if (!OFF_POSITIONS.includes(p.posKey)) return;
    if (!teamMap[p.team]) teamMap[p.team] = Object.fromEntries(OFF_POSITIONS.map(pos => [pos, []]));
    teamMap[p.team][p.posKey].push(p);
  });
  const scores = {};
  Object.entries(teamMap).forEach(([team, byPos]) => {
    let total = 0;
    OFF_POSITIONS.forEach(pos => {
      const { nrMap, maxRank } = posData[pos];
      const slots = OFF_POS_SLOTS[pos] ?? 1;
      const nrs = (byPos[pos] || []).map(p => nrMap[p.id] ?? maxRank).sort((a,b) => a-b).slice(0, slots);
      while (nrs.length < slots) nrs.push(maxRank);
      total += nrs.reduce((s,n) => s+n, 0) / slots;
    });
    scores[team] = total / OFF_POSITIONS.length;
  });
  return scores;
}

// ── Defensive computation (mirrors DefensiveTeamNR.js) ───────────────────────
const DEF_FORMULAS = {
  DE: ['sacks','QBHits','tacklesForLoss','totalTackles','forcedFumbles'],
  DT: ['sacks','QBHits','tacklesForLoss','totalTackles','forcedFumbles'],
  LB: ['totalTackles','sacks','tacklesForLoss','passesDefended','forcedFumbles'],
  CB: ['passesDefended','interceptions','totalTackles'],
  S:  ['totalTackles','passesDefended','interceptions'],
};
const DEF_POSITIONS = ['DE','DT','LB','CB','S'];
const DEF_POS_SLOTS = { DE: 2, DT: 2, LB: 3, CB: 2, S: 2 };
const DEF_MIN_GAMES = 6;

function computeDefPosNR(pos, players, defStats, weights = {}) {
  const statKeys = DEF_FORMULAS[pos];
  const eligible = players.filter(p => defStats[p.id]?.gamesPlayed >= DEF_MIN_GAMES).map(p => {
    const s = defStats[p.id]; const gp = s.gamesPlayed;
    const perGame = {};
    statKeys.forEach(k => { perGame[k] = gp > 0 ? s[k] / gp : 0; });
    return { ...p, perGame };
  });
  const maxRank = eligible.length + 1;
  if (!eligible.length) { const m = {}; players.forEach(p => { m[p.id] = maxRank; }); return { nrMap: m, maxRank }; }
  const statRanks = {}; eligible.forEach(p => { statRanks[p.id] = {}; });
  statKeys.forEach(k => {
    const sorted = [...eligible].sort((a, b) => b.perGame[k] - a.perGame[k]);
    let rank = 1, i = 0;
    while (i < sorted.length) {
      const tieVal = sorted[i].perGame[k]; let j = i;
      while (j < sorted.length && sorted[j].perGame[k] === tieVal) { statRanks[sorted[j].id][k] = rank; j++; }
      rank += j - i; i = j;
    }
  });
  const nrMap = {};
  const withNR = eligible.map(p => ({ ...p, rankSum: statKeys.reduce((acc, k) => acc + (statRanks[p.id][k] ?? eligible.length) * (weights[k] ?? 1), 0) })).sort((a, b) => a.rankSum - b.rankSum);
  let nr = 1, i = 0;
  while (i < withNR.length) {
    const tieSum = withNR[i].rankSum; let j = i;
    while (j < withNR.length && withNR[j].rankSum === tieSum) { nrMap[withNR[j].id] = nr; j++; } nr += j - i; i = j;
  }
  players.forEach(p => { if (!(p.id in nrMap)) nrMap[p.id] = maxRank; });
  return { nrMap, maxRank };
}

function computeTeamDefScores(allPlayers, defStats, config) {
  const posData = {};
  DEF_POSITIONS.forEach(pos => {
    const posPlayers = Object.values(allPlayers).filter(p => p.posKey === pos);
    posData[pos] = computeDefPosNR(pos, posPlayers, defStats, config?.[`nr_weights_def_${pos.toLowerCase()}`] ?? {});
  });
  const teamMap = {};
  Object.values(allPlayers).forEach(p => {
    if (!DEF_POSITIONS.includes(p.posKey)) return;
    if (!teamMap[p.team]) teamMap[p.team] = Object.fromEntries(DEF_POSITIONS.map(pos => [pos, []]));
    teamMap[p.team][p.posKey].push(p);
  });
  const scores = {};
  Object.entries(teamMap).forEach(([team, byPos]) => {
    let total = 0;
    DEF_POSITIONS.forEach(pos => {
      const { nrMap, maxRank } = posData[pos];
      const slots = DEF_POS_SLOTS[pos] ?? 1;
      const nrs = (byPos[pos] || []).map(p => nrMap[p.id] ?? maxRank).sort((a,b) => a-b).slice(0, slots);
      while (nrs.length < slots) nrs.push(maxRank);
      total += nrs.reduce((s,n) => s+n, 0) / slots;
    });
    scores[team] = total / DEF_POSITIONS.length;
  });
  return scores;
}

// ── Styles ────────────────────────────────────────────────────────────────────
const cellSx = { color: '#9eb8d0', fontSize: '0.75rem', borderColor: 'rgba(30,52,72,0.5)', py: 0.5, px: 0.75 };
const headerCellSx = { color: '#a78bfa', fontWeight: 700, fontSize: '0.7rem', borderColor: 'rgba(30,52,72,0.5)', py: 0.5, px: 0.75, backgroundColor: '#060e1a' };

const TEAM_NAMES = {
  ARI:'Cardinals', ATL:'Falcons', BAL:'Ravens', BUF:'Bills',
  CAR:'Panthers', CHI:'Bears', CIN:'Bengals', CLE:'Browns',
  DAL:'Cowboys', DEN:'Broncos', DET:'Lions', GB:'Packers',
  HOU:'Texans', IND:'Colts', JAX:'Jaguars', KC:'Chiefs',
  LAC:'Chargers', LAR:'Rams', LV:'Raiders', MIA:'Dolphins',
  MIN:'Vikings', NE:'Patriots', NO:'Saints', NYG:'Giants',
  NYJ:'Jets', PHI:'Eagles', PIT:'Steelers', SEA:'Seahawks',
  SF:'49ers', TB:'Buccaneers', TEN:'Titans', WSH:'Commanders',
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function TeamNROverall() {
  const { config } = useConfig();
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [players, setPlayers]   = useState({});
  const [offStats, setOffStats] = useState({});
  const [defStats, setDefStats] = useState({});

  useEffect(() => {
    setLoading(true);
    loadBase()
      .then(({ players: p }) => {
        setPlayers(p);
        const offIds = Object.keys(p).filter(id => ['QB','RB','WR','TE'].includes(p[id].posKey));
        const defIds = Object.keys(p).filter(id => ['DE','DT','LB','CB','S'].includes(p[id].posKey));
        return Promise.all([loadPlayerStats(offIds), loadDefensiveStats(defIds)]);
      })
      .then(([off, def]) => { setOffStats(off); setDefStats(def); setLoading(false); })
      .catch(() => { setError('Failed to load team data.'); setLoading(false); });
  }, []);

  const teamRankings = useMemo(() => {
    if (!Object.keys(players).length || !Object.keys(offStats).length || !Object.keys(defStats).length) return [];

    const offScores = computeTeamOffScores(players, offStats, config);
    const defScores = computeTeamDefScores(players, defStats, config);

    // All teams from either map
    const allTeams = new Set([...Object.keys(offScores), ...Object.keys(defScores)]);
    const maxOff = Math.max(...Object.values(offScores), 20);
    const maxDef = Math.max(...Object.values(defScores), 20);

    const rows = [...allTeams].map(team => {
      const off = offScores[team] ?? maxOff;
      const def = defScores[team] ?? maxDef;
      const combined = (off + def) / 2;
      return { team, combined, off, def };
    });

    rows.sort((a, b) => a.combined - b.combined);
    return rows.map((r, i) => ({ ...r, teamNR: i + 1 }));
  }, [players, offStats, defStats, config]);

  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
      <CircularProgress size={36} sx={{ color: '#a78bfa' }} />
      <Typography variant="body2" sx={{ color: '#9eb8d0' }}>Computing overall team rankings…</Typography>
    </Box>
  );

  if (error) return <Box sx={{ p: 3 }}><Typography sx={{ color: '#ef4444' }}>{error}</Typography></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#e8f0f8' }}>
          Overall Team Nerd Rating
        </Typography>
        <Chip label="Nerds+" color="secondary" size="small" sx={{ fontWeight: 800, fontSize: '0.65rem', height: 20 }} />
      </Box>
      <Typography variant="caption" sx={{ color: '#6a8aa8', display: 'block', mb: 2.5, lineHeight: 1.6 }}>
        Combined score = (Offensive Team NR score + Defensive Team NR score) ÷ 2. Lower = better overall team.
        Offensive NR uses QB1·RB1+2·WR1+2+3·TE1+2 starters; Defensive NR uses DE1+2·DT1+2·LB1+2+3·CB1+2·S1+2.
      </Typography>

      <TableContainer component={Paper} sx={{ backgroundColor: '#060e1a', border: '1px solid rgba(30,52,72,0.6)', borderRadius: 1.5 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellSx, width: 48 }}>NR</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 130 }}>Team</TableCell>
              <TableCell align="right" sx={{ ...headerCellSx, width: 80 }}>Combined ↓</TableCell>
              <TableCell align="right" sx={{ ...headerCellSx, width: 72, color: '#f5a623' }}>Off Score</TableCell>
              <TableCell align="right" sx={{ ...headerCellSx, width: 72, color: '#fb7185' }}>Def Score</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {teamRankings.map((row, idx) => (
              <TableRow key={row.team} sx={{
                backgroundColor: idx % 2 === 0 ? 'rgba(6,14,26,0.5)' : 'rgba(10,20,38,0.4)',
                '&:hover': { backgroundColor: 'rgba(22,44,70,0.6)' },
              }}>
                <TableCell sx={{
                  ...cellSx, fontWeight: 900, width: 48, fontSize: '0.82rem',
                  color: row.teamNR <= 5 ? '#a78bfa' : row.teamNR <= 10 ? '#f5a623' : '#e8f0f8',
                }}>
                  #{row.teamNR}
                </TableCell>

                <TableCell sx={{ ...cellSx, width: 130 }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#e8f0f8', lineHeight: 1.2 }}>
                    {TEAM_NAMES[row.team] ?? row.team}
                  </Typography>
                  <Typography sx={{ fontSize: '0.62rem', color: '#4a6080', lineHeight: 1.2 }}>
                    {row.team}
                  </Typography>
                </TableCell>

                <TableCell align="right" sx={{ ...cellSx, fontWeight: 700, color: '#e8f0f8' }}>
                  {row.combined.toFixed(1)}
                </TableCell>

                <TableCell align="right" sx={{ ...cellSx, color: '#f5a623' }}>
                  {row.off.toFixed(1)}
                </TableCell>

                <TableCell align="right" sx={{ ...cellSx, color: '#fb7185' }}>
                  {row.def.toFixed(1)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
